/**
 * supabaseClient.js
 *
 * One shared Supabase client for the whole app, built from the public
 * "anon" key.
 *
 * That key is SAFE to ship inside client-side JS — anyone can see it
 * in the built bundle, and that's fine by design. The anon key only
 * identifies which Supabase project to talk to; it grants no access
 * by itself. Actual permissions are enforced by Postgres Row Level
 * Security (RLS) policies (see supabase/schema.sql), which are set up
 * to allow exactly one thing from this key: inserting a new wallet
 * row. Reading, editing, or deleting rows is denied at the database
 * level no matter what the client-side code does.
 *
 * Never do the same with the "service_role" key — that one bypasses
 * RLS entirely and must only ever be used from a trusted server.
 *
 * Create React App only exposes env vars prefixed REACT_APP_ to the
 * browser bundle, and only reads them at build time. Copy .env.example
 * to .env, fill in your project's values from
 * Supabase Dashboard → Project Settings → API, then restart `npm start`
 * (CRA does not hot-reload env var changes).
 */
import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  // eslint-disable-next-line no-console
  console.warn(
    '[supabase] REACT_APP_SUPABASE_URL / REACT_APP_SUPABASE_ANON_KEY are ' +
      'missing. Wallet submissions will fail until they are set in .env ' +
      '(see .env.example) and the dev server is restarted.'
  );
}

/**
 * True only when both values are present. Callers should check this
 * before relying on a query: a client built from empty strings does not
 * fail fast — its requests can hang, which is far worse than a clean
 * "not configured", because anything awaiting it never settles.
 */
export const isSupabaseConfigured = Boolean(supabaseUrl && supabaseAnonKey);

export const supabase = createClient(supabaseUrl || '', supabaseAnonKey || '');
